import { BaseAIService } from '../BaseAIService';
import { AIServiceOptions, AIServiceResponse, AzureOpenAIConfig } from '../types';
import { handleStreamResponse } from '../utils/StreamHandler';
import { parseErrorResponse } from '../utils/ErrorHandler';

interface AzureOpenAIStreamResponse {
  choices?: Array<{
    delta?: {
      content?: string;
    };
  }>;
}

export class AzureOpenAIService extends BaseAIService {
  private config: AzureOpenAIConfig;
  
  constructor(config: AzureOpenAIConfig = {}) {
    super('azureopenai');
    this.config = config;
  }
  
  async generateText(options: AIServiceOptions): Promise<AIServiceResponse> {
    if (!this.config.apiKey || !this.config.endpoint || !this.config.deploymentName) {
      return {
        text: '',
        error: 'Azure OpenAI 配置不完整，请检查 API 密钥、端点和部署名称'
      };
    }
    
    try {
      const messages = this.createMessages(options.prompt, options.systemPrompt);
      
      const url = `${this.config.endpoint}/openai/deployments/${this.config.deploymentName}/chat/completions?api-version=2023-05-15`;
      const headers = {
        'Content-Type': 'application/json',
        'api-key': this.config.apiKey as string
      };
      
      const body = {
        messages,
        temperature: options.temperature ?? this.config.temperature ?? 0.7,
        max_tokens: options.maxTokens || this.config.maxTokens,
        top_p: options.topP ?? this.config.topP ?? 1,
        stream: !!options.onUpdate,
      };
      
      // 如果有实时更新回调，使用流式响应
      if (options.onUpdate) {
        const text = await handleStreamResponse({
          url,
          headers,
          body,
          signal: options.abortSignal,
          onUpdate: options.onUpdate,
          errorPrefix: 'Azure OpenAI 请求失败',
          parseContent: (data: unknown) => {
            const response = data as AzureOpenAIStreamResponse;
            return response.choices?.[0]?.delta?.content || '';
          }
        });
        
        return { text };
      } else {
        // 非流式响应
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
          signal: options.abortSignal
        });
        
        if (!response.ok) {
          throw await parseErrorResponse(this.provider, response);
        }
        
        const data = await response.json();
        return {
          text: data.choices[0]?.message?.content || ''
        };
      }
    } catch (error) {
      return this.handleError(error);
    }
  }
} 